import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Type, Palette, Layers, Volume2, Shirt } from 'lucide-react';

const types = [
  {
    icon: Type,
    name: 'Word Mark',
    description: 'Protects the text of your brand name, slogan, or phrase regardless of font, color, or design style.',
    example: 'e.g. "NIKE", "JUST DO IT"',
    color: 'from-purple-500 to-violet-600',
    iconBg: 'bg-purple-100 text-[#7C3AED]',
  },
  {
    icon: Palette,
    name: 'Design Mark',
    description: 'Protects a specific logo, symbol, or graphic element used to identify your brand visually.',
    example: 'e.g. Apple logo, Nike swoosh',
    color: 'from-teal-500 to-emerald-600',
    iconBg: 'bg-teal-100 text-[#14B8A6]',
  },
  {
    icon: Layers,
    name: 'Composite Mark',
    description: 'Combines both text and design into a single trademark, protecting the entire combination together.',
    example: 'e.g. Starbucks name + logo',
    color: 'from-violet-500 to-purple-600',
    iconBg: 'bg-violet-100 text-violet-600',
  },
  {
    icon: Volume2,
    name: 'Sound Mark',
    description: 'Protects a unique sound or jingle that identifies your brand in the marketplace.',
    example: 'e.g. NBC chimes, Intel bong',
    color: 'from-emerald-500 to-teal-600',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  {
    icon: Shirt,
    name: 'Trade Dress',
    description: 'Protects the overall visual appearance or packaging of a product that signals its source.',
    example: 'e.g. Coca-Cola bottle shape',
    color: 'from-purple-600 to-indigo-600',
    iconBg: 'bg-indigo-100 text-indigo-600',
  },
];

const TypeCard = ({ type, index }) => {
  const card = useScrollAnimation({ threshold: 0.1 });
  const Icon = type.icon;

  return (
    <div
      ref={card.ref}
      data-testid={`trademark-type-${index}`}
      className={`group relative bg-white rounded-2xl p-7 border border-zinc-100 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(124,58,237,0.15)] hover:-translate-y-2 overflow-hidden scroll-fade-up scroll-delay-${(index % 3) + 1} ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${type.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

      <div className={`w-12 h-12 rounded-xl ${type.iconBg} flex items-center justify-center mb-5`}>
        <Icon className="w-6 h-6" />
      </div>

      <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {type.name}
      </h3>
      <p className="text-sm text-zinc-500 leading-relaxed mb-3">
        {type.description}
      </p>
      <span className="text-xs text-[#7C3AED] font-medium bg-[#7C3AED]/5 px-3 py-1 rounded-full">
        {type.example}
      </span>
    </div>
  );
};

const TrademarkTypesSection = () => {
  const header = useScrollAnimation({ threshold: 0.15 });

  return (
    <section data-testid="trademark-types-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-[#7C3AED]/3 blur-[120px]" />

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#7C3AED]/10 text-[#7C3AED] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
            TRADEMARK TYPES
          </span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            What Can You Trademark?
          </h2>
          <p className="text-base text-zinc-500 leading-relaxed">
            The USPTO recognizes several types of trademarks. Understanding which type applies to your brand helps ensure proper protection.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {types.slice(0, 3).map((type, i) => (
            <TypeCard key={i} type={type} index={i} />
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-5 max-w-2xl mx-auto lg:max-w-none lg:grid-cols-2 lg:px-[16.7%]">
          {types.slice(3).map((type, i) => (
            <TypeCard key={i + 3} type={type} index={i + 3} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrademarkTypesSection;
